import { create } from 'zustand';
import type { SearchResultRecord } from '@shared/types';
import { getDesktopApi } from '@renderer/lib/desktop-api';

type SearchStatus = 'idle' | 'loading' | 'ready' | 'error';

interface SearchStore {
  isOpen: boolean;
  query: string;
  results: SearchResultRecord[];
  activeIndex: number;
  status: SearchStatus;
  error: string | null;
  requestVersion: number;
  open: () => void;
  close: () => void;
  setQuery: (query: string) => void;
  loadResults: (query: string) => Promise<void>;
  moveActiveIndex: (direction: -1 | 1) => void;
  setActiveIndex: (index: number) => void;
}

function getNextActiveIndex(currentIndex: number, length: number, direction: -1 | 1): number {
  if (length === 0) {
    return -1;
  }

  if (currentIndex === -1) {
    return direction === 1 ? 0 : length - 1;
  }

  return (currentIndex + direction + length) % length;
}

export const useSearchStore = create<SearchStore>((set, get) => ({
  isOpen: false,
  query: '',
  results: [],
  activeIndex: -1,
  status: 'idle',
  error: null,
  requestVersion: 0,
  open: () => set({ isOpen: true }),
  close: () =>
    set({
      isOpen: false,
      query: '',
      results: [],
      activeIndex: -1,
      status: 'idle',
      error: null
    }),
  setQuery: (query) => set({ query }),
  loadResults: async (query) => {
    const trimmedQuery = query.trim();
    const requestVersion = get().requestVersion + 1;
    set({
      requestVersion,
      status: trimmedQuery ? 'loading' : 'idle',
      error: null,
      results: trimmedQuery ? get().results : [],
      activeIndex: trimmedQuery ? get().activeIndex : -1
    });

    if (!trimmedQuery) {
      set({
        results: [],
        activeIndex: -1,
        status: 'idle',
        error: null
      });
      return;
    }

    try {
      const results = await getDesktopApi().search.querySearchIndex({ query: trimmedQuery, limit: 8 });

      if (get().requestVersion !== requestVersion) {
        return;
      }

      set({
        results,
        activeIndex: results.length > 0 ? 0 : -1,
        status: 'ready',
        error: null
      });
    } catch (error) {
      if (get().requestVersion !== requestVersion) {
        return;
      }

      set({
        results: [],
        activeIndex: -1,
        status: 'error',
        error: error instanceof Error ? error.message : 'Search failed.'
      });
    }
  },
  moveActiveIndex: (direction) =>
    set((state) => ({
      activeIndex: getNextActiveIndex(state.activeIndex, state.results.length, direction)
    })),
  setActiveIndex: (activeIndex) => set({ activeIndex })
}));
